import { RootStore } from './root.store';
import { makeAutoObservable } from 'mobx';
import { isEmailValid, isPhoneNumberValid } from '../utils/validators';
import { ContactType, IContact } from '../domains/common.domain';

export class ContactsStore {
  root: RootStore;
  private _contacts: IContact[] = [];
  private _hideEmail: boolean = false;
  private _hidePhone: boolean = false;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.resetContacts();
    this.root = root;
  }

  init(type?: ContactType) {
    switch (type) {
      case 'admin':
        this._hidePhone = true;
        break;
      case 'shipping':
        this._hideEmail = this.root.tenantStore.config.notDisplayShippingEmail;
        break;
    }
    this.resetContacts();
  }

  get contacts(): IContact[] {
    return this._contacts;
  }

  set contacts(contacts: IContact[]) {
    this._contacts = contacts.map(x => ({
      ...x,
      uiKey: crypto.randomUUID(),
      email: this._hideEmail ? { value: '' } : { ...x.email },
      phoneNumber: this._hidePhone ? { value: '' } : { ...x.phoneNumber },
    }));
  }

  get hideEmail(): boolean {
    return this._hideEmail;
  }

  get hidePhone(): boolean {
    return this._hidePhone;
  }

  resetContacts() {
    this._contacts = [this._newContact];
  }

  addContact() {
    this._contacts.push(this._newContact);
  }

  removeContact(index: number) {
    this._contacts.splice(index, 1);
  }

  updateContact(index: number, field: keyof IContact, value: string) {
    if (field === 'uiKey') return;

    this._contacts[index][field] = {
      value: value,
      errorType: !value ? 'required' : undefined,
    };
    if (value) {
      switch (field) {
        case 'email':
          this._contacts[index].email.errorType = this._hideEmail || isEmailValid(value) ? undefined : 'invalid';
          break;
        case 'phoneNumber':
          this._contacts[index].phoneNumber.errorType =
            this._hidePhone || isPhoneNumberValid(value, this.root.tenantStore.config.minPhoneNumberLength) ? undefined : 'invalid';
          break;
      }
    }
  }

  get isFormValid(): boolean {
    return this._contacts.every(contact => {
      return !contact.firstName.errorType && !contact.lastName.errorType && !contact.email.errorType && !contact.phoneNumber?.errorType;
    });
  }

  private get _newContact(): IContact {
    return {
      uiKey: crypto.randomUUID(),
      firstName: {
        value: '',
        errorType: 'required',
      },
      lastName: {
        value: '',
        errorType: 'required',
      },
      email: {
        value: '',
        errorType: this._hideEmail ? undefined : 'required',
      },
      phoneNumber: {
        value: '',
        errorType: this._hidePhone ? undefined : 'required',
      },
    };
  }
}
